import { EventType } from '@graphql/generated-base';
import type { GuestListRoute } from './guestListRoute.types';

export type { GuestListRoute, GuestListRouteSegment } from './guestListRoute.types';

/**
 * Resolves the `/edit/<segment>` guest list route for an event.
 *
 * `guests` is the legacy monorepo route. It reads Firebase, so it 404s for any event that has no
 * Firebase document behind it — every new-stack wedding. General events already live on the
 * Firebase-free guest list (see `general_default.ts`), so they take the same branch.
 *
 * A new-stack wedding (`isNewStackEvent`), a general event and a card event all need the
 * Firebase-free guest list instead. Card is included because `useEventSubTypeConfig` maps
 * `EventType.card` to the general workspace config, whose guest list link is already the
 * Firebase-free one — without card here the dashboard tile and the nav would disagree: `guestlistv2` when both rollout flags are on, or
 * `guestlist` otherwise. An unresolved `eventType` defaults to `guests`, so an old-stack wedding
 * keeps today's behavior while its event info is still loading.
 *
 * `guestlist` is a joy-web SPA route (registered in `Admin.controller.tsx`), so `pathIsInJoyWeb` is
 * `true`. `guestlistv2` is server/S3-served and needs a hard navigation instead — see
 * `SERVER_SERVED_EDIT_SEGMENTS` in `returnNavigation.ts` — so its `pathIsInJoyWeb` is `false`, the
 * same as the legacy `guests` route.
 */
export const resolveGuestListRoute = (
  args: Readonly<{
    eventType: EventType | null | undefined;
    isNewStackEvent: boolean;
    isGuestListV2Enabled: boolean;
    useDirectGuestListV2: boolean;
  }>,
): GuestListRoute => {
  const { eventType, isNewStackEvent, isGuestListV2Enabled, useDirectGuestListV2 } = args;

  const needsFirebaseFreeGuestList = isNewStackEvent || eventType === EventType.general || eventType === EventType.card;
  if (!needsFirebaseFreeGuestList) {
    return { segment: 'guests', pathIsInJoyWeb: false };
  }

  if (isGuestListV2Enabled && useDirectGuestListV2) {
    return { segment: 'guestlistv2', pathIsInJoyWeb: false };
  }

  return { segment: 'guestlist', pathIsInJoyWeb: true };
};
